import React from 'react';
import { styled } from 'styled-components';
import checkmark from '@ingka/ssr-icon/paths/checkmark';
import SSRIcon from '@ingka/ssr-icon';
import locationPin from '@ingka/ssr-icon/paths/location-pin';
import clipboardCheckmark from '@ingka/ssr-icon/paths/clipboard-checkmark';
import documentCheckmark from '@ingka/ssr-icon/paths/document-checkmark';
import calendar from '@ingka/ssr-icon/paths/calendar';
import moneyCash from '@ingka/ssr-icon/paths/money-cash';
import person from '@ingka/ssr-icon/paths/person';
import Text from '@ingka/text';
import Price from '@ingka/price';
import { useTranslation } from 'react-i18next';
import PageContent from '../components/PageContent';
import PayOrder from '../components/PayOrder';
import QuestionLink from '../components/QuestionLink';
import { mediaQueries } from '../styles/mediaQueries';
import colors from '../styles/colors';
import {
  isServiceProvidedRemote,
  type ServiceLocation,
} from '../helpers/appHelper';
import { useStateSelector } from '../store/';
import InfoMessage from '../components/InfoMessage';
import CustomerFeedbackSurvey from './CustomerFeedbackSurvey';
import type { AvailableService } from '../services/appointment/model';
import type { AppView } from '../types/app';

interface ConfirmDetailsProps {
  orderNo: string;
  service: AvailableService | undefined;
  serviceLocation: ServiceLocation | undefined;
  showPayOrder: boolean;
  timeText: string;
  addToCalendar: React.ReactNode;
  locationAddress: string;
  locationName: string;
  view: AppView;
}

const StyledParagraph = styled.p`
  margin-bottom: 1rem;

  ${mediaQueries.portraitTabletUp} {
    margin-bottom: 2.5rem;
  }
`;

const Top = styled.section`
  text-align: center;
  margin: 0 auto;
`;

const StyledIcon = styled(SSRIcon)`
  &&& {
    color: ${colors.semantic.positive};
    width: 88px;
    height: 88px;
  }
`;

const ConfirmDetailsTitle = styled(Text)`
  &&& {
    padding: 0;
    margin-bottom: 1rem;
    margin-block: 2.5rem 16px;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;

    ${mediaQueries.desktopUp} {
      margin-block: 3.5rem 16px;
    }
  }
`;

const Details = styled.div`
  padding: 0;
  margin-block: 0 2.5rem;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px;

  ${mediaQueries.desktopUp} {
    margin-block: 0 3.5rem;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const DetailsItem = styled.div`
  width: 100%;

  ${mediaQueries.desktopUp} {
    &:nth-child(odd) {
      grid-column: 1;
    }
    &:nth-child(even) {
      grid-column: 2;
    }
  }
`;

const CustomerSurveyContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const ConfirmDetails: React.FC<ConfirmDetailsProps> = ({
  orderNo,
  service,
  serviceLocation,
  showPayOrder,
  timeText,
  addToCalendar,
  locationName,
  locationAddress,
  view,
}) => {
  const { t } = useTranslation();
  const contact = useStateSelector((state) => state.contact);
  const additionalInfoLinkEnabled = useStateSelector(
    (state) => state.app.booking.additionalInfoLinkEnabled,
  );
  const questionsToken = useStateSelector(
    (state) => state.app.booking.questionsToken,
  );
  const payOrderLink = useStateSelector(
    (state) => state.app.booking.payOrderLink,
  );

  const hasContactInfo = (): boolean => {
    return !!contact.firstName;
  };

  const getContactInfo = (): string[] => {
    const userName = t('pages.confirm.userName', {
      firstName: contact.firstName,
      surName: contact.surName,
    });
    const contactInfo = [contact.mobileNumber || '', contact.email || ''];
    if (contact.businessName) {
      contactInfo.unshift(contact.businessName);
    }
    return [userName, ...contactInfo];
  };

  const servicePrice = () => {
    if (!service) return [];
    const { priceFormatted, price } = service.customerPrice;

    if (price > 0) {
      const { mainPrice, secondaryPrice } = priceFormatted;
      const prices = [
        <Price key="main-price" {...mainPrice} fixedSize size="small" subtle />,
      ];
      if (secondaryPrice) {
        prices.push(
          <Price
            key="secondary-price"
            {...secondaryPrice}
            fixedSize
            size="small"
            subtle
          />,
        );
      }
      return prices;
    }
    return [t('pages.confirm.freePrice')];
  };

  return (
    <>
      <PageContent>
        <Top>
          <StyledIcon paths={checkmark} />
          <Text tagName="h2" textStyle="Heading.L">
            {t('pages.confirm.title')}
          </Text>
          <StyledParagraph>{t('pages.confirm.description')}</StyledParagraph>
          {!showPayOrder && (
            <CustomerSurveyContainer>
              <CustomerFeedbackSurvey />
            </CustomerSurveyContainer>
          )}
        </Top>
        {showPayOrder && service && (
          <PayOrder
            price={service.customerPrice}
            currencyCode={service.displayPrice.currencyCode}
            payOrderLink={payOrderLink}
            orderNo={orderNo}
            email={contact.email}
            page="confirm"
            view={view}
          />
        )}
        {questionsToken && additionalInfoLinkEnabled && (
          <QuestionLink
            token={questionsToken}
            buttonType={showPayOrder ? 'primary' : 'emphasised'}
          />
        )}

        <ConfirmDetailsTitle tagName="h2" textStyle="Heading.S">
          {t('pages.confirm.confirmDetailsTitle')}
        </ConfirmDetailsTitle>
        <Details data-cy="confirm-details">
          <DetailsItem>
            {orderNo && (
              <InfoMessage
                icon={documentCheckmark}
                iconAriaLabel={t('pages.confirm.icons.orderNo')}
                message={[`${t('pages.confirm.bookingId')} ${orderNo}`]}
                id="order-number"
              />
            )}
          </DetailsItem>
          <DetailsItem>
            <InfoMessage
              icon={clipboardCheckmark}
              iconAriaLabel={t('pages.confirm.icons.serviceName')}
              message={[t(`services.name.${service?.serviceId}`)]}
              id="service-name"
            />
          </DetailsItem>
          <DetailsItem>
            <InfoMessage
              icon={calendar}
              iconAriaLabel={t('pages.confirm.icons.dateAndTime')}
              message={[timeText]}
              id="service-time"
            />
          </DetailsItem>
          {addToCalendar && <DetailsItem>{addToCalendar}</DetailsItem>}
          <DetailsItem>
            {(locationName || locationAddress) && serviceLocation && (
              <InfoMessage
                icon={locationPin}
                iconAriaLabel={t('pages.confirm.icons.location')}
                message={[locationName, locationAddress]}
                id={
                  isServiceProvidedRemote(serviceLocation)
                    ? 'service-location-remote'
                    : 'service-location'
                }
              />
            )}
          </DetailsItem>
          <DetailsItem>
            <InfoMessage
              icon={moneyCash}
              iconAriaLabel={t('pages.confirm.icons.price')}
              message={servicePrice()}
              id="service-price"
            />
          </DetailsItem>
          {hasContactInfo() && (
            <DetailsItem>
              <InfoMessage
                icon={person}
                iconAriaLabel={t('pages.confirm.icons.customer')}
                message={getContactInfo()}
                id="user-contact-data"
                data-cs-mask
              />
            </DetailsItem>
          )}
        </Details>
      </PageContent>
    </>
  );
};

export default ConfirmDetails;
